// src/rms/components/SetupProgress.jsx
// ============================================================================
// Setup progress
// ============================================================================
// Onboarding asks four questions and then gets out of the way. Everything else
// a business actually needs -- tax above all -- is configured from here, where
// there is room to explain it and no pressure to guess.
//
// Two rules shape this list.
//
// Every item is DISMISSIBLE. A single-person tool-hire shop is never going to
// invite staff, and a bar that can only ever reach 78% stops being a guide and
// becomes wallpaper. Dismissing is not the same as completing: the item goes
// grey and leaves the count, and it comes back the moment the thing is
// genuinely done.
//
// Nothing here BLOCKS. An operator can take a booking on day one with no tax
// configured, because most are trying the product before committing to it. Tax
// gets a banner on Billing instead, where a missing rate actually matters, and
// invoices raised without one say so on their face.
// ============================================================================

import React from "react";
import { Link } from "react-router-dom";
import { useRms } from "../context/RmsContext";

export default function SetupProgress({ items, onDismiss, onRestore, collapsed, onToggle }) {
  const { t } = useRms();

  const live = items.filter((i) => !i.dismissed);
  const done = live.filter((i) => i.done).length;
  const pct = live.length ? Math.round((done / live.length) * 100) : 100;
  const complete = done === live.length;

  // Done is done: the whole card goes, and does not come back.
  //
  // This used to hold on whenever anything had been dismissed, so that a
  // dismissed item could still be put back. The cost was a permanent panel on
  // the dashboard of exactly the businesses that had been most decisive about
  // what they did not need -- a one-person tool-hire shop that waves away
  // "invite your staff" finishes everything else and reads "5 of 5 done"
  // forever. A checklist that never leaves has stopped being a checklist.
  //
  // Nothing is stranded by this. An item that gets genuinely done later stops
  // counting as dismissed (see the dashboard's setupItems), so the only thing
  // now unreachable is "put back" on something the operator already said they
  // did not want, on a business that is fully set up.
  if (complete) return null;

  return (
    <div className="rms-card rms-card-pad" style={{ marginBottom: 20 }}>
      {/* The whole header is the toggle. A checklist you have read once should
          fold away without hunting for a small chevron, and the bar and count
          stay visible collapsed so it still reports progress at a glance. */}
      <button type="button" className="rms-setup-head" onClick={onToggle} aria-expanded={!collapsed}>
        {/* Drawn, not a font glyph: ▾ and ▼ render at wildly different weights
            across platforms, and at 11px the light one all but vanished.
            Points DOWN when collapsed (click to open) and UP when expanded
            (click to close), which is also the right way round under RTL --
            vertical direction does not mirror. */}
        <svg className="rms-setup-caret" width="11" height="7" viewBox="0 0 11 7" aria-hidden="true" focusable="false">
          <path d={collapsed ? "M0 0 L5.5 7 L11 0 Z" : "M0 7 L5.5 0 L11 7 Z"} fill="currentColor" />
        </svg>
        <h2 className="rms-section-title" style={{ margin: 0, flex: 1, textAlign: "start" }}>
          {t("setup.title", "Finish setting up")}
        </h2>
        <span style={{ fontSize: 12.5, color: "var(--rms-muted-strong)" }}>
          {t("setup.progress", "{done} of {total} done", { done, total: live.length })}
        </span>
      </button>

      <div
        className="rms-setup-bar"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t("setup.title", "Finish setting up")}
      >
        <span style={{ width: `${pct}%` }} />
      </div>

      {collapsed ? null : (
      <ul className="rms-setup-list">
        {items.map((item) => (
          <li key={item.key} className={`rms-setup-item${item.dismissed ? " is-dismissed" : ""}`}>
            <span className={`rms-setup-tick${item.done ? " is-done" : ""}`} aria-hidden="true">
              {item.done ? "✓" : ""}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              {item.done || item.dismissed ? (
                <span className="rms-setup-label">{item.label}</span>
              ) : item.onAction ? (
                // Some steps have nowhere to send you. Setting a PIN is the
                // one that matters: the screen it used to link to is itself
                // behind a PIN, so a member without one was sent to a door
                // that could only be opened by the thing they were going there
                // to create.
                <button
                  type="button"
                  className="rms-setup-label rms-setup-link rms-linkish"
                  onClick={item.onAction}
                >
                  {item.label}
                </button>
              ) : (
                <Link to={item.to} className="rms-setup-label rms-setup-link">
                  {item.label}
                </Link>
              )}
              {item.hint && !item.done ? <span className="rms-setup-hint">{item.hint}</span> : null}
            </span>
            {item.done ? null : item.dismissed ? (
              <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => onRestore(item.key)}>
                {t("setup.restore", "Put back")}
              </button>
            ) : (
              <button
                type="button"
                className="rms-btn rms-btn--ghost rms-btn--sm"
                onClick={() => onDismiss(item.key)}
                title={t("setup.dismissHelp", "Hide this if your business does not need it.")}
              >
                {t("setup.dismiss", "Not needed")}
              </button>
            )}
          </li>
        ))}
      </ul>
      )}
    </div>
  );
}
